import { Stripe, loadStripe } from '@stripe/stripe-js';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../store';

let cachedStripePromise: Promise<Stripe | null> | null = null;
let lastLoadedKey: string | null = null;

export function useStripeInstance() {
    const stripePublishableKey = useSelector((state: RootState) => state.brand.brandDetails?.StripePublishableKey);
    const testKey =
        'pk_test_51SaHwdQXzhvEJB1QbTpCRPPFfQQEgP2AGes6cDWylOjLYFNiTufvKlxNulLHb7CnLp5qL2Zeyf9rIcxSYP60Td3z0068ktHr9h';
    const keyToUse = stripePublishableKey || testKey;

    useEffect(() => {
        if (!keyToUse) {
            console.warn(
                '[useStripeInstance] publishable key not available in brand details. Ensure brand API is loaded before accessing Stripe.',
            );
            cachedStripePromise = null;
            lastLoadedKey = null;
            return;
        }

        // If key changed, reset cached Stripe instance
        if (keyToUse !== lastLoadedKey) {
            console.log('[useStripeInstance] Stripe key changed or first load, resetting cache.');
            cachedStripePromise = null;
            lastLoadedKey = keyToUse;
        }

        if (!cachedStripePromise) {
            console.log(
                '[useStripeInstance] Initializing Stripe.js with publishable key (prefix:',
                keyToUse.slice(0, 20) + '...',
                ')',
            );
            cachedStripePromise = loadStripe(keyToUse).then(instance => {
                if (!instance) {
                    console.error(
                        '[useStripeInstance] Stripe.js initialization failed. Possible causes: invalid publishable key, network error, or Stripe.js CDN unavailable.',
                    );
                } else {
                    console.log('[useStripeInstance] Stripe.js initialized successfully');
                }
                return instance;
            });
        }
    }, [keyToUse]);

    if (!cachedStripePromise && keyToUse) {
        console.warn(
            '[useStripeInstance] useEffect not yet run but key is available. This component might render before Stripe initializes.',
        );
    }

    return cachedStripePromise;
}

export function useIsStripeConfigured(): boolean {
    const stripePublishableKey = useSelector((state: RootState) => state.brand.brandDetails?.StripePublishableKey);
    const testKey =
        'pk_test_51SaHwdQXzhvEJB1QbTpCRPPFfQQEgP2AGes6cDWylOjLYFNiTufvKlxNulLHb7CnLp5qL2Zeyf9rIcxSYP60Td3z0068ktHr9h';
    return Boolean(stripePublishableKey || testKey);
}
